import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { updateBroadcast, uploadFiles } from 'api/broadcasts.service';
import { useToast } from 'hooks/ui';
import { useAppLocation } from 'hooks/helpers';
import { broadcastRoutes } from 'router/broadcastRoutes';
import { Broadcast } from 'models/broadcast.model';
import { isLocalFile, isServerAttachment } from 'interfaces';
import { useApplicationStore } from 'store/application';
import { setDraftBroadcast } from 'store/broadcast';
import { broadcastKeys } from './queryKeys';

type MutationFnArgs = {
  id: string;
  broadcast: Broadcast;
};

export const useBroadcastUpdateQuery = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { setLoading, removeLoading } = useApplicationStore((state) => state);
  const { isCreate } = useAppLocation(broadcastRoutes);

  const { isPending, mutate } = useMutation({
    mutationFn: async ({ id, broadcast }: MutationFnArgs) => {
      setLoading();

      return updateBroadcast(id, {
        title: broadcast.title,
        message: broadcast.message,
        tagIds: broadcast.tagIds,
        schedule: broadcast.schedule,
        attachments: broadcast.attachments?.filter(isServerAttachment),
      }).then(async (res) => {
        const attachments = broadcast.attachments?.filter(isLocalFile);

        if (attachments?.length) {
          await uploadFiles(res.id, attachments);
        }

        return res;
      });
    },
    onSuccess: (broadcast) => {
      toast.success('Broadcast has been drafted successfully');
      if (!isCreate) setDraftBroadcast(broadcast);
    },
    onError: (error: AxiosError) => toast.error(error.message),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: broadcastKeys.all });
      removeLoading();
    },
  });

  return { isLoading: isPending, mutate };
};
